/**
 * Double-tap detection for touch, shared by every surface that wants a
 * double-click gesture to also work on a phone.
 *
 * Touch has no `dblclick` a page can rely on, so the two-tap gesture is
 * detected by hand: remember when and where the first tap ENDED, then treat a
 * second tap as a double when it lands close enough in both time and space.
 *
 * Two rules every call site follows:
 *
 * 1. Record candidates on `pointerup`, never `pointerdown` — a press that
 *    turns into a drag must not count as the first half of a double-tap.
 * 2. Pair this with React's `onDoubleClick`, which is what fires for a mouse.
 *    `onPointerUp` handlers bail on `e.pointerType === "mouse"` so a desktop
 *    double-click is not counted twice.
 *
 * The thresholds and the predicate lived inline in `CalendarTimeline` (and
 * were copy-pasted into `Calendar`'s month grid, then wanted a third time by
 * the mobile member cards). One definition, one test.
 */

/** Max gap between the two taps of a double-tap, and max finger travel. */
export const DOUBLE_TAP_MS = 350;
export const DOUBLE_TAP_SLOP_PX = 24;

/** One recorded tap: when it ended, where it ended, and what it landed on. */
export interface TapCandidate {
  /** `PointerEvent.timeStamp` of the `pointerup`. */
  readonly t: number;
  readonly x: number;
  readonly y: number;
  /**
   * Identity of the thing tapped — a calendar day key, a member row id. BOTH
   * taps must name the same target, so two quick taps on neighbouring cards
   * are two single taps, not one double.
   */
  readonly key: string;
}

/**
 * True when `next` completes a double-tap begun by `prev`: same target, inside
 * `DOUBLE_TAP_MS`, and no more than `DOUBLE_TAP_SLOP_PX` of finger travel on
 * either axis. A null `prev` (nothing pending) is never a double.
 */
export function isDoubleTap(
  prev: TapCandidate | null,
  next: TapCandidate,
): boolean {
  if (prev === null) return false;
  if (prev.key !== next.key) return false;
  return (
    next.t - prev.t < DOUBLE_TAP_MS &&
    Math.abs(next.x - prev.x) < DOUBLE_TAP_SLOP_PX &&
    Math.abs(next.y - prev.y) < DOUBLE_TAP_SLOP_PX
  );
}
